import { Box } from '@mui/material'
import CustomGrid from 'common/components/CustomGrid'
import Table from 'common/components/Table'
import { CustomModalContext } from 'common/contexts/CustomModalContext'
import { ModalTypes } from 'common/hooks/useCustomModal'
import DataTableSkeleton from 'features/solicitudes/components/skeletons/DataTableSkeleton'
import { useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useFetchInformeData from '../hooks/useFetchInformeData'
import { InformesContext } from '../providers/InformesProvider'
import CalendarView from './CalendarView'
import InformeCard from './InformeCard'
import InformePluginButton from './InformePluginButton'

const InformeDataTable = ({ ejecutar_consulta }) => {
  const location = useLocation()

  const { handleModal } = useContext(CustomModalContext)
  const {
    active: {
      current,
      structure,
      activeFilter,
      setActiveFilter,
      iteration,
      page,
      forceRefresh,
      setPage,
      hasEmptyForced,
      calendarView,
    },
    previous: { previousReport },
  } = useContext(InformesContext)
  const [tableStructure, setTableStructure] = useState()
  const [pageSize, setPageSize] = useState(12)

  const {
    response,
    loading = false,
    error,
  } = useFetchInformeData({
    nombre_informe: current?.nombre_informe ?? null,
    filtro: activeFilter,
    limit: pageSize,
    page: page,
    iteration: iteration,
    worker: location?.state?.worker ?? null,
    ejecutar_consulta: ejecutar_consulta,
  })

  const [hashedResponse, setHashedResponse] = useState({})
  const prevActiveFilterRef = useRef({})
  const prevPreviousReport = useRef({})

  // Comprobar si la pagina contiene registro en hashedResponse
  const checkPageValues = (pageToCheck) => {
    return (
      Array.isArray(hashedResponse[pageToCheck]) &&
      hashedResponse[pageToCheck].some((obj) => obj && Object.keys(obj).length > 0)
    )
  }

  useEffect(() => {
    const newHash = { ...hashedResponse }
    //Comprobamos que exista alguna linea con valores en la pagina
    if (checkPageValues(page)) return

    // Asegurar que existan las páginas previas vacías (retorno desde hijo)
    for (let i = 1; i < page; i++) {
      if (!newHash[i]) {
        newHash[i] = []
        for (let x = 0; x < pageSize; x++) {
          newHash[i][x] = {}
        }
      }
    }
    newHash[page] = response?.registros ?? []
    setHashedResponse(newHash)
  }, [response])

  useEffect(() => {
    setHashedResponse({})
    // Solo modificamos la pagina si existe un cambio real en los filtros y no viene de un hijo
    if (
      JSON.stringify(prevActiveFilterRef?.current) !== JSON.stringify(activeFilter) &&
      Object.values(prevPreviousReport?.current).length === 0 &&
      Object.values(hashedResponse)?.length > 0
    ) {
      setPage(1)
    }
    prevPreviousReport.current = previousReport
    prevActiveFilterRef.current = activeFilter
  }, [activeFilter])

  const data = (() => {
    const newVal = Object.values(hashedResponse)?.flat() || []
    //Comprobamos valores para la pagina en funcion del tamaño
    return newVal?.[page != 1 ? (page - 1) * pageSize : 0] ? newVal : []
  })()

  useEffect(() => {
    handleModal({ enabled: loading, type: ModalTypes.Loading })
  }, [loading])

  useEffect(() => {
    setTableStructure(null)
    if (!structure?.response?.columnas) return

    /** @type {Array<import('common/components/SimpleTable').SimpleTableField>} **/
    const newFields = structure.response.columnas.filter((item) => {
      return item.ancho_web > 0
    })

    const maxCount = structure?.response?.plugins?.length ?? 0

    let fields = newFields.map((item, index) => {
      const width =
        maxCount !== 0
          ? `calc(${item.ancho_web}% - ${Math.ceil(35 / (newFields.length + 1))}px)`
          : `${item.ancho_web}`

      return {
        title: item.titulo_columna,
        field: item.alias_columna,
        cellStyle: {
          padding: '5px',
          fontSize: '.9em',
          width,
          maxWidth: width,
        },
        headerStyle: {
          textAlign: 'left',
          // whiteSpace: 'nowrap',
          padding: '5px',
          fontSize: '.9em',
          width,
          maxWidth: width,
        },
      }
    })

    if (maxCount !== 0) {
      fields = [
        {
          cellStyle: {
            padding: '10px 5px',
            display: 'block',
            fontSize: '.9em',
            width: `35px`,
          },
          headerStyle: {
            textAlign: 'left',
            // whiteSpace: 'nowrap',
            padding: '5px',
            fontSize: '.9em',
            width: `35px`,
            display: 'block',
          },
          customGetter: (row) => (
            <>
              <InformePluginButton plugins={structure?.response?.plugins ?? []} element={row} />
            </>
          ),
          title: ' ',
        },
        ...fields,
      ]
    }

    setTableStructure(fields)
  }, [structure.response])

  return (
    <>
      {tableStructure && !structure.loading ? (
        <Box sx={{ display: tableStructure && !structure?.loading ? 'initial' : 'none' }}>
          {!calendarView ? (
            <>
              <Table
                sx={{ display: { xs: 'none', lg: 'block' } }}
                fields={tableStructure}
                dataSource={data ?? []}
                stripped
                count={response?.numero_registros}
                page={page}
                loading={loading || structure.loading}
                onPageChanged={(newPage) => {
                  if (checkPageValues(newPage)) return
                  setPage(newPage)
                }}
                rowsPerPage={pageSize}
                onRowsPerPageChanged={(value) => {
                  setPageSize(value)
                  setPage(1)
                  setHashedResponse({})
                  forceRefresh()
                }}
                header_cell_style={{ p: 2 }}
                emptyText={
                  error && hasEmptyForced
                    ? 'Por favor rellena los filtros obligatorios (*)'
                    : error
                      ? 'Ha ocurrido un error'
                      : loading
                        ? 'Cargando'
                        : 'No hay datos'
                }
              />
              <CustomGrid
                sx={{ display: { xs: 'block', lg: 'none' } }}
                dataSource={data ?? []}
                dataStructure={tableStructure}
                count={response?.numero_registros}
                page={page}
                loading={loading || structure.loading}
                pageLimit={pageSize}
                CardLayout={InformeCard}
                onPageChanged={(newPage) => {
                  if (checkPageValues(newPage)) return
                  setPage(newPage)
                }}
                gridProps={{ spacing: 2 }}
                gridItemsProps={{ size: { xs: 12, md: 6 } }}
              />
            </>
          ) : (
            <CalendarView nombre_informe={current?.nombre_informe} activeFilter={activeFilter} />
          )}
        </Box>
      ) : current?.nombre_informe ? (
        <DataTableSkeleton />
      ) : (
        <></>
      )}
    </>
  )
}

export default InformeDataTable
